import { PushPin } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useQuickAccessMatters, useMatterQuickAccessStore } from '@/lib/matterQuickAccessStore';

/**
 * The strip of pinned matters at the top of a matters list.
 *
 * Pinning happens on the matter's own page, and its toast promises "find it on the Matters
 * page" — so every matters list has to honour that. This lived inside the admin list only,
 * which left a barrister pinning a matter and finding nothing waiting for them (the row
 * floated to the top with a small icon, but nothing said "Pinned"). Shared so the two lists
 * cannot drift apart again.
 *
 * Renders nothing when no matter is pinned, and `useQuickAccessMatters` returns an empty
 * list unless the cache belongs to the signed-in user — another user's pins never surface.
 */
export function PinnedMattersBar({
  basePath,
  navigate,
}: {
  basePath: string;
  navigate: (path: string) => void;
}) {
  const { pinned } = useQuickAccessMatters();
  const unpinMatter = useMatterQuickAccessStore((s) => s.unpinMatter);
  if (pinned.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <PushPin className="h-3.5 w-3.5" weight="fill" /> Pinned
      </span>
      {pinned.map((m) => (
        <div
          key={m.id}
          role="button"
          tabIndex={0}
          onClick={() => navigate(`${basePath}/matters/${m.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`${basePath}/matters/${m.id}`);
            }
          }}
          className="group flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:border-brand-400 cursor-pointer"
        >
          <span>{m.number ? `${m.number} · ` : ''}{m.name}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              unpinMatter(m.id);
              toast.success('Matter unpinned');
            }}
            className="ml-1 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
            title="Unpin"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
